import { BasicConstants } from "../constants/BasicConstants";
import { ForcedMaintenanceEnum } from "./ForcedMaintenanceEnum";
import { ExternalInterface } from "@goodgamestudios/createjs-utilities";

export const paramToString = (val: string = null, _default: string = "") => val ? val : _default;
export const paramToBool = (val: string = null, _default: boolean = false): boolean => {
    if (!val) {
        return _default;
    }
    if (!isNaN(Number(val))) {
        return val !== "0";
    }
    return val.toLowerCase() === "true";
};
export const paramToNum = (val: string = null, _default: number = 0): number => {
    if (!val) {
        return _default;
    }
    const n = Number(val);
    if (!isNaN(n)) {
        return n;
    }
    return _default;
};

import { getLogger } from "@goodgamestudios/logger";

const logger = getLogger("CoreJS.BasicURLVariables");
const UNINITIALIZED = "not-set";
export class BasicURLVariables {
    private _initialized: boolean = false;

    /** Contains all URL GET parameters **/
    private _params: URLSearchParams;

    private _instance: number = 0;
    private _country: string;
    private _forceToShowTestServers: boolean;
    private _emulateLaggyClient: boolean = false;
    private _forceManualLogin: boolean = false;
    private _forcedMaintenance: ForcedMaintenanceEnum;
    private _websiteId: string =UNINITIALIZED;
    private _lpWebsiteId: string = UNINITIALIZED;
    private _loginSource: string = UNINITIALIZED;
    private _laggyClientMinDelay: number = 50;
    private _laggyClientMaxDelay: number = 250;
    private _testNetConf: boolean = false;
    private _forceToShowLogger: boolean = false;
    private _friendInviteCode: string = "";
    private _friendInviteZoneId: number = 0;
    private _friendInviteInviterCountryCode: string = "";

    /**
     * Initialize URL Variables with query parameters coming from Landing Page.
     * The `queryParameters` must be loaded from Landing Page through `ggsGetQueryParameters` during the initialization proccess.
     * The `queryParameters` are mixed with the parameters passed through `iframe` URL, so the URLVariables will handle all parameters.
     * @param queryParameters query parameters loaded coming from ggsGetQueryParameters.
     */
    protected initParams(queryParameters?: string): void {
        if (this._initialized) {
            return;
        }

        this._initialized = true;

        queryParameters = (queryParameters || "").replace("?", "");

        let searchParams: string = (queryParameters.length > 0) ? [window.location.search, queryParameters].join("&") : window.location.search;

        this._params = new URLSearchParams(searchParams);

        this.parseParameters();
    }

    /**
     *
     */
    private parseParameters(): void {
        //----------------------------------
        //  ACTIVATE NETCONF TEST
        //----------------------------------
        // check if the _testNetConf parameter is set. If true network and country xml were loaded from the games-config-livetest directory
        this._testNetConf = paramToBool(this._params.get(BasicConstants.NETCONF_TEST_PARAMETER));

        //----------------------------------
        //	FRIEND INVITE CODE
        //----------------------------------
        if (this._params.has(BasicConstants.FRIEND_INVITE_CODE)) {
            this._friendInviteCode = paramToString(this._params.get(BasicConstants.FRIEND_INVITE_CODE));

            // Friend Invite Code has the format: zoneId-playerId
            let friendInviteCodeParts: any[] = this._friendInviteCode.split("-");

            if (friendInviteCodeParts.length > 0) {
                let zoneIdEncoded: string = friendInviteCodeParts[0];

                // Decode the zoneId part
                this._friendInviteZoneId = parseInt(zoneIdEncoded, 36);

                // Validate zone id
                this._friendInviteZoneId = isNaN(this._friendInviteZoneId) ? 0 : this._friendInviteZoneId;
            } else {
                this._friendInviteZoneId = 0;
            }

            let countryCodePattern: RegExp = /[a-zA-Z]+/;
            let inviterCountryCode: string;

            // Parse inviter country code part
            if (friendInviteCodeParts.length === 3) {
                inviterCountryCode = friendInviteCodeParts[2];
            } else if (friendInviteCodeParts.length === 4) {
                inviterCountryCode = friendInviteCodeParts[3];
            } else {
                inviterCountryCode = "";
            }

            // Validate country code part
            inviterCountryCode = countryCodePattern.test(inviterCountryCode) ? inviterCountryCode : "";

            // Define the country code part
            this._friendInviteInviterCountryCode = inviterCountryCode;
        }

        //----------------------------------
        //  Activate Logger
        //----------------------------------
        // check if logger should be activated. If true, activate Logger and log all infos
        this._forceToShowLogger = paramToBool(this._params.get(BasicConstants.FORCE_TO_SHOW_LOGGER));

        /**
         * laggy client min command delay
         */
        this._laggyClientMinDelay = paramToNum(this._params.get(BasicConstants.LAGGY_CLIENT_MIN_DELAY), 50);
        logger.debug("laggy client min delay has been set to " + this._laggyClientMinDelay + " ms");

        /**
         * laggy client max command delay
         */
        this._laggyClientMaxDelay = paramToNum(this._params.get(BasicConstants.LAGGY_CLIENT_MAX_DELAY), 250);
        logger.debug("laggy client max delay has been set to " + this._laggyClientMaxDelay + " ms");

        // Check `useMinAssets` parameter from query string and store it on window
        (<any>window).useMinAssets = paramToBool(this._params.get(BasicConstants.USE_MIN_ASSETS), true);
    }

    /**
     * Read QueryParameters from Landing Page.
     */
    public readQueryParametersFromLandingPage(): Promise<any> {
        if (!ExternalInterface.available) {
            this.initParams();
            return Promise.resolve();
        }

        // Initialize once
        if (this._initialized) {
            return Promise.resolve("already initialized");
        }

        return new Promise((resolve, reject) => {
            ExternalInterface.asyncCall("ggsGetQueryParameters")
                .then(result => {
                    this.initParams(result);
                    resolve();
                })
                .catch(reason => {
                    this.initParams();
                    resolve();
                });
        });
    }

    public get urlParams(): URLSearchParams {
        return this._params;
    }

    public get presetInstanceId(): number {
        if (!this._instance) {
            this._instance = paramToNum(this._params.get("instance"));
        }
        return this._instance;
    }

    /** Named in url "country" **/
    public get country(): string {
        if (!this._country) {
            this._country = paramToString(this._params.get(BasicConstants.COUNTRY_PARAMETER));
        }
        return this._country;
    }

    /** Named in url "forceToShowTestServers" **/
    public get forceToShowTestServers(): boolean {
        if (!this._forceToShowTestServers) {
            this._forceToShowTestServers = paramToBool(this._params.get(BasicConstants.FORCE_TO_SHOW_TEST_SERVERS));
        }
        return this._forceToShowTestServers;
    }

    /** Named in url "emulateLaggyClient" **/
    public get emulateLaggyClient(): boolean {
        if (!this._emulateLaggyClient) {
            this._emulateLaggyClient = paramToBool(this._params.get(BasicConstants.COMMAND_EMULATE_LAGGY_CLIENT));
        }
        return this._emulateLaggyClient;
    }

    /** Named in url "forceManualLogin" **/
    public get forceManualLogin(): boolean {
        if (!this._forceManualLogin) {
            this._forceManualLogin = paramToBool(this._params.get(BasicConstants.FORCE_MANUAL_LOGIN));
        }
        return this._forceManualLogin;
    }

    /** Named in url "maintenance" **/
    public get forcedMaintenance(): ForcedMaintenanceEnum {
        if (!this._forcedMaintenance) {
            if (this._params.has(BasicConstants.MAINTENANCE_PARAMETER)) {
                const val = this._params.get(BasicConstants.MAINTENANCE_PARAMETER);

                if (val === "on") {
                    this._forcedMaintenance = ForcedMaintenanceEnum.ON;
                }
                else if (val === "off") {
                    this._forcedMaintenance = ForcedMaintenanceEnum.OFF;
                }
                else {
                    logger.warn(`${val} is not an accepted param for {maintenance} - please use on/off`);
                    this._forcedMaintenance = ForcedMaintenanceEnum.NOT_SET;
                }
            } else {
                this._forcedMaintenance = ForcedMaintenanceEnum.NOT_SET;
            }
            return this._forcedMaintenance;
        }
    }

    /**
     * <p>Referring partner site id (websiteId).</p>
     * <p>Default value = 0</p>
     * @see http://goo.gl/xdsY7l googledocs
     */
    public get websiteId(): string {
        if (this._websiteId == UNINITIALIZED) {
            this._websiteId = paramToString(this._params.get(BasicConstants.PARTNER_WEBSITE), "0");
        }
        return this._websiteId;
    }

    /**
     * <p>Referring partner site id when from landigpage(lpWebsiteId).</p>
     * <p>Default value = 0</p>
     */
    public get lpWebsiteId(): string {
        if (this._lpWebsiteId == UNINITIALIZED) {
            this._lpWebsiteId = paramToString(this._params.get(BasicConstants.LP_PARTNER_WEBSITE), "0");
        }
        return this._lpWebsiteId;
    }

    public get extendedWebsiteID(): string {
        if (this.websiteId !== "0") {
            return this.websiteId;
        }
        if (this.lpWebsiteId !== "0") {
            return this.lpWebsiteId;
        }
        return "0";
    }

    /**
     * <p>Login Source</p>
     * <p>Default value = 0</p>
     */
    public get loginSource(): string {
        if (this._loginSource == UNINITIALIZED ) {
            this._loginSource = paramToString(this._params.get(BasicConstants.LOGIN_SOURCE), "0");
        }
        return this._loginSource;
    }

    /** Named in url "laggyClientMinDelay" **/
    public get laggyClientMinDelay(): number {
        return this._laggyClientMinDelay;
    }

    /** Named in url "laggyClientMaxDelay" **/
    public get laggyClientMaxDelay(): number {
        return this._laggyClientMaxDelay;
    }

    /** Named in url "testNetConf" **/
    public get testNetConf(): boolean {
        return this._testNetConf;
    }

    /** Named in url "forceToShowLogger"**/
    public get forceToShowLogger(): boolean {
        return this._forceToShowLogger;
    }

    /** Friend Invite Code - is passed when the user received a invite from a friend **/
    public get friendInviteCode(): string {
        return this._friendInviteCode;
    }

    public get friendInviteZoneId(): number {
        return this._friendInviteZoneId;
    }

    public get friendInviteInviterCountryCode(): string {
        return this._friendInviteInviterCountryCode;
    }
}
